// Schema-driven TransactionCleanup with dynamic table rendering
import React, { useState, useEffect } from 'react';
import { Filter, Search, Edit, X, Columns } from 'lucide-react';
import CloseNav from '../nav';
import MessagePanel from '../close/messagePanel';
import DynamicTable from 'components/table/DynamicTable';
import BulkUpdateModal from 'components/bulk/BulkUpdateModal';
import RecordDetails from 'components/recordDetails/RecordDetails';
import { LINE_ITEM_UPDATE_MODES } from 'components/bulk/BulkUpdateModal';
import schemaService from 'services/SchemaService';
import { useAPI } from 'components/lib';
import styles from './transactionCleanup.module.css';
import { KanbanBoard } from './kanbanBoard';
import axios from 'axios';

export function TransactionCleanup() {
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [schemaAvailable, setSchemaAvailable] = useState(false);
  const [activeMessageTransaction, setActiveMessageTransaction] = useState(null);
  const [messageThread, setMessageThread] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [bulkUpdateMode, setBulkUpdateMode] = useState(false);
  const [schemas, setSchemas] = useState({});
  const [lookupData, setLookupData] = useState({});
  const [viewDetailsRecord, setViewDetailsRecord] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'kanban'

  // API hooks
  const transactionsAPI = useAPI(`/api/appdata/records/transactions`);
  
  // Sync API data with local state like original transactionCleanup
  useEffect(() => {
    if (transactionsAPI.data) {
      setTransactions(transactionsAPI.data);
    }
  }, [transactionsAPI.data]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => {
        setToastMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);
  
  // Initialize schema service with graceful fallback
  useEffect(() => {
    const initializeSchemas = async () => {
      try {
        setLoading(true);
        await schemaService.initialize();
        setSchemaAvailable(true);
      } catch (error) {
        console.error('Schema service initialization failed:', error);
        setSchemaAvailable(false);
        // Do not continue without schema service - show error state
      } finally {
        setLoading(false);
      }
    };

    initializeSchemas();
  }, []);

  // Load schemas and lookup data when schema service is available
  useEffect(() => {
    const loadSchemasAndLookups = async () => {
      if (!schemaAvailable) return;
      
      try {
        const recordTypes = [...new Set(transactions.map(r => {
          return Array.isArray(r.record_type) ? r.record_type[0] : r.record_type;
        }))].filter(Boolean);

        const schemaMap = {};
        const allLookupData = {};

        for (const recordType of recordTypes) {
          try {
            const schema = schemaService.getSchemaForRecord(recordType);
            schemaMap[recordType] = schema;
            
            const lookups = await schemaService.getRequiredLookupData(recordType);
            Object.assign(allLookupData, lookups);
          } catch (error) {
            console.error(`Failed to load schema for ${recordType}:`, error);
            schemaMap[recordType] = null;
          }
        }

        setSchemas(schemaMap);
        setLookupData(allLookupData);
      } catch (error) {
        console.error('Failed to load schemas and lookup data:', error);
      }
    };

    loadSchemasAndLookups();
  }, [schemaAvailable, transactions]);

  // Handle record selection
  const handleRecordSelect = (recordId) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(recordId)) {
      newSelected.delete(recordId);
    } else {
      newSelected.add(recordId);
    }
    setSelectedRecords(newSelected);
  };

  // Handle record updates - Exactly like original transactionCleanup
  const handleRecordUpdate = async (recordId, changes) => {
    try {
      // Update local state immediately like original transactionCleanup
      setTransactions(prev => prev.map(transaction => {
        if (transaction.id === recordId) {
          return {
            ...transaction,
            ...changes
          };
        }
        return transaction;
      }));
      
      // Validate changes against schema if available
      if (schemaAvailable) {
        const record = transactions.find(r => r.id === recordId);
        if (record) {
          const errors = [];
          Object.entries(changes).forEach(([fieldName, value]) => {
            try {
              if (fieldName !== 'rec_normalized') { // Skip validation for nested updates
                const validation = schemaService.validateFieldValue(record.record_type, fieldName, value);
                if (!validation.valid) {
                  errors.push({ field: fieldName, error: validation.error });
                }
              }
            } catch (e) {
              console.warn('Schema validation failed for field:', fieldName, e);
            }
          });
          
          if (errors.length > 0) {
            throw new Error(`Validation errors: ${errors.map(e => `${e.field}: ${e.error}`).join(', ')}`);
          }
        }
      }
      
      // Send all updates to server - let Python handle normalized vs regular updates
      const response = await axios.patch('/api/appdata/record', {
        record_id: recordId,
        updates: changes
      });
      
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`Failed to update record: ${response.statusText}`);
      }
      
      // Show success toast
      setToastMessage('Update is being processed...');
      
    } catch (error) {
      console.error('Failed to update record:', error);
      throw error;
    }
  };

  // Helper function to extract searchable text from a value
  const extractSearchableText = (value) => {
    if (!value) return '';
    
    if (typeof value === 'string' || typeof value === 'number') {
      return value.toString();
    }
    
    if (typeof value === 'object' && !Array.isArray(value)) {
      // For objects, extract name, display_name, or value fields
      return value.name || value.display_name || value.DisplayName || value.value || '';
    }
    
    if (Array.isArray(value)) {
      // For arrays, search through each item
      return value.map(item => extractSearchableText(item)).join(' ');
    }
    
    return '';
  };

  // Helper function to recursively search through all fields in a record
  const searchRecord = (obj, searchTerm, visited = new Set()) => {
    if (!obj || typeof obj !== 'object' || visited.has(obj)) return false;
    visited.add(obj);
    
    const lowerSearchTerm = searchTerm.toLowerCase();
    
    for (const [key, value] of Object.entries(obj)) {
      // Skip certain fields to avoid noise
      if (key.startsWith('_') || key === 'MetaData' || key === 'CustomField') {
        continue;
      }
      
      const searchableText = extractSearchableText(value);
      if (searchableText && searchableText.toLowerCase().includes(lowerSearchTerm)) {
        return true;
      }
      
      // Recursively search nested objects (but avoid infinite loops)
      if (typeof value === 'object' && value !== null) {
        if (searchRecord(value, searchTerm, visited)) {
          return true;
        }
      }
    }
    
    return false;
  };

  // Filter records based on search and filters
  const filteredRecords = React.useMemo(() => {
    if (!transactions.length) return [];
    
    return transactions.filter(record => {
      // Search filter - now searches all fields in the record
      if (searchTerm) {
        const normalizedData = record.rec_normalized || {};
        const rawData = record;
        
        // Search both normalized and raw data
        const matchesNormalized = searchRecord(normalizedData, searchTerm);
        const matchesRaw = searchRecord(rawData, searchTerm);
        
        if (!matchesNormalized && !matchesRaw) {
          return false;
        }
      }
      
      // Additional filters
      if (filters.recordType) {
        // Handle both string and array record_type
        const recordType = Array.isArray(record.record_type) ? record.record_type[0] : record.record_type;
        if (recordType !== filters.recordType) {
          return false;
        }
      }
      
      if (filters.status && record.status !== filters.status) {
        return false;
      }
      
      return true;
    });
  }, [transactions, searchTerm, filters]);

  // Get available filter options from data
  const getFilterOptions = () => {
    if (!transactions.length) return { recordTypes: [], statuses: [] };
    
    // Handle both string and array record_type values to get unique types
    const recordTypes = [...new Set(transactions.map(r => {
      // Handle both string and array record_type
      return Array.isArray(r.record_type) ? r.record_type[0] : r.record_type;
    }).filter(Boolean))];
    
    const statuses = [...new Set(transactions.map(r => r.status).filter(Boolean))];
    
    return { recordTypes, statuses };
  };

  const { recordTypes, statuses } = getFilterOptions();

  // Handle opening message panel like v1
  const openMessagePanel = (transaction) => {
    setActiveMessageTransaction(transaction);
    // Filter to show only internal messages
    const internalMessages = (transaction.messages || []).filter(msg => msg.isInternal !== false);
    setMessageThread(internalMessages);
  };

  // Handle resolve toggle like v1
  const toggleResolved = (transactionId) => {
    setTransactions(prev =>
      prev.map(t => {
        if (t.id === transactionId) {
          return { ...t, resolved: !t.resolved };
        }
        return t;
      })
    );
  };

  // Handle view details
  const handleViewDetails = (record) => {
    setViewDetailsRecord(record);
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: messageThread.length + 1,
      sender: 'bookkeeper',
      message: newMessage,
      timestamp: new Date().toLocaleString(),
      isInternal: true
    };
    
    setMessageThread([...messageThread, message]);
    setNewMessage('');
  };

  // Bulk update helper functions
  const getSelectedRecordType = () => {
    if (selectedRecords.size === 0) return null;
    
    const selectedTx = filteredRecords.filter(r => selectedRecords.has(r.id));
    if (selectedTx.length === 0) return null;
    
    const recordTypes = [...new Set(selectedTx.map(r => 
      Array.isArray(r.record_type) ? r.record_type[0] : r.record_type
    ))].filter(Boolean);
    
    return recordTypes.length === 1 ? recordTypes[0] : null;
  };

  const updateRecordLineItems = (record, lineItemUpdates) => {
    const { updates, mode, matchingCriteria } = lineItemUpdates;
    const currentLineItems = record.rec_normalized?.line_items || [];
    
    switch (mode) {
      case LINE_ITEM_UPDATE_MODES.ALL_ITEMS:
        return currentLineItems.map(item => ({ ...item, ...updates }));
        
      case LINE_ITEM_UPDATE_MODES.FIRST_ITEM:
        if (currentLineItems.length === 0) return currentLineItems;
        return [
          { ...currentLineItems[0], ...updates },
          ...currentLineItems.slice(1)
        ];
        
      case LINE_ITEM_UPDATE_MODES.MATCHING_CRITERIA:
        return currentLineItems.map(item => {
          const matches = Object.entries(matchingCriteria).every(([field, value]) => {
            if (value === '' || value === null || value === undefined) return true;
            return item[field] === value;
          });
          return matches ? { ...item, ...updates } : item;
        });
        
      case LINE_ITEM_UPDATE_MODES.ADD_NEW_ITEM:
        const newItem = { id: Date.now(), ...updates };
        return [...currentLineItems, newItem];
        
      default:
        return currentLineItems;
    }
  };

  const handleBulkUpdate = async (bulkUpdates) => {
    try {
      const selectedTx = filteredRecords.filter(r => selectedRecords.has(r.id));
      const { recordUpdates, lineItemUpdates } = bulkUpdates;
      
      // Apply updates optimistically
      const updatedTransactions = [...transactions];
      
      for (const record of selectedTx) {
        const index = updatedTransactions.findIndex(t => t.id === record.id);
        if (index === -1) continue;
        
        let updatedRecord = { ...updatedTransactions[index] };
        
        // Apply record-level updates
        if (recordUpdates && Object.keys(recordUpdates).length > 0) {
          updatedRecord = { ...updatedRecord, ...recordUpdates };
        }
        
        // Apply line item updates
        if (lineItemUpdates) {
          const updatedLineItems = updateRecordLineItems(updatedRecord, lineItemUpdates);
          updatedRecord = {
            ...updatedRecord,
            rec_normalized: {
              ...updatedRecord.rec_normalized,
              line_items: updatedLineItems
            }
          };
        }
        
        updatedTransactions[index] = updatedRecord;
      }
      
      // Update state optimistically
      setTransactions(updatedTransactions);
      
      // Send bulk update to server using axios
      const response = await axios.patch('/api/appdata/records/bulk-update', {
        record_ids: selectedTx.map(r => r.id),
        record_updates: recordUpdates,
        line_item_updates: lineItemUpdates
      });
      
      if (!response.ok) {
        throw new Error(`Bulk update failed: ${response.statusText}`);
      }
      
      console.log(`Successfully updated ${selectedTx.length} records`);
      
    } catch (error) {
      console.error('Bulk update failed:', error);
      // Revert optimistic update by refetching data
      if (transactionsAPI.refetch) {
        transactionsAPI.refetch();
      }
      alert(`Bulk update failed: ${error.message}`);
      throw error; // Re-throw to let the modal handle the error
    }
  };

  const handleBulkUpdateComplete = () => {
    setBulkUpdateMode(false);
    setSelectedRecords(new Set());
  };

  // Ensure bulk update mode doesn't auto-open inappropriately 
  const handleBulkUpdateTrigger = () => {
    if (canBulkUpdate && selectedRecords.size > 1) {
      setBulkUpdateMode(true);
    }
  };

  // Check if bulk update is possible
  const selectedRecordType = getSelectedRecordType();
  const canBulkUpdate = selectedRecords.size > 1 && selectedRecordType !== null;

  if (loading) {
    return (
      <div className={styles.container}>
        <CloseNav />
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div>Loading service...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!schemaAvailable) {
    return (
      <div className={styles.container}>
        <CloseNav />
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.errorMessage}>
              <h2>Schema Service Unavailable</h2>
              <p>The schema definitions could not be loaded from the server.</p>
              <p>Please check the browser console for detailed error information.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <CloseNav />
      
      {!viewDetailsRecord && (
        <>
          {/* Header */}
          <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerActions}>
            <div className={styles.searchContainer}>
              <Search className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Search records..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>
            
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={styles.filterButton}
            >
              <Filter className={styles.filterIcon} />
              <span>Filters</span>
            </button>
            
            {/* View Mode Toggle */}
            {/* <button
              onClick={() => setViewMode(viewMode === 'table' ? 'kanban' : 'table')}
              className={styles.viewModeButton}
              title={`Switch to ${viewMode === 'table' ? 'Kanban' : 'Table'} view`}
            >
              <Columns className={styles.viewModeIcon} />
              <span>{viewMode === 'table' ? 'Kanban Board' : 'Table View'}</span>
            </button> */}
            
            {/* Bulk Update Actions */}
            {selectedRecords.size > 0 && (
              <>
                {canBulkUpdate && selectedRecords.size > 1 && (
                  <button 
                    onClick={handleBulkUpdateTrigger}
                    className={styles.bulkUpdateButton}
                  >
                    <Edit className={styles.editIcon} />
                    Bulk Update
                  </button>
                )}
                
                <button 
                  onClick={() => setSelectedRecords(new Set())}
                  className={styles.clearButton}
                  title="Clear selection"
                >
                  <X className={styles.clearIcon} />
                  Clear
                </button>
              </>
            )}
          </div>
          
          {/* Record Count and Selection Info */}
          <div className={styles.recordInfo}>
            <span className={styles.recordCount}>
              Showing {filteredRecords.length} of {transactions.length || 0} records
            </span>
            {selectedRecords.size > 0 && (
              <span className={styles.selectionInfo}>
                • {selectedRecords.size} selected
                {selectedRecordType && ` (${selectedRecordType})`}
              </span>
            )}
            {!canBulkUpdate && selectedRecords.size > 1 && (
              <span className={styles.selectionWarning}>
                • Selected records must be the same type for bulk update
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className={styles.filtersPanel}>
          <div className={styles.filtersContent}>
            <select
              value={filters.recordType || ''}
              onChange={(e) => setFilters({...filters, recordType: e.target.value || undefined})}
              className={styles.filterSelect}
            >
              <option value="">All Record Types</option>
              {recordTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            
            <select
              value={filters.status || ''}
              onChange={(e) => setFilters({...filters, status: e.target.value || undefined})}
              className={styles.filterSelect}
            >
              <option value="">All Statuses</option>
              {statuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Content View */}
      {viewMode === 'table' ? (
        // Dynamic Table View
        transactions.length > 0 ? (
          <DynamicTable
            records={filteredRecords}
            selectedRecords={selectedRecords}
            onRecordSelect={handleRecordSelect}
            onRecordUpdate={handleRecordUpdate}
            onOpenMessagePanel={openMessagePanel}
            onToggleResolved={toggleResolved}
            onViewDetails={handleViewDetails}
            maxColumns={8}
          />
        ) : transactionsAPI.loading ? (
          <div className={styles.loading}>Loading transaction data...</div>
        ) : (
          <div className={styles.noData}>No transaction data available</div>
        )
      ) : (
        // Kanban Board View
        <div className={styles.kanbanWrapper}>
          <KanbanBoard />
        </div>
      )}

      {/* Message Panel */}
      <MessagePanel
        activeMessageTransaction={activeMessageTransaction}
        setActiveMessageTransaction={setActiveMessageTransaction}
        messageThread={messageThread}
        newMessage={newMessage}
        setNewMessage={setNewMessage}
        sendMessage={sendMessage}
      />

          {/* Bulk Update Modal */}
          {bulkUpdateMode && selectedRecordType && selectedRecords.size > 1 && (
            <BulkUpdateModal
              isOpen={bulkUpdateMode}
              selectedRecords={filteredRecords.filter(r => selectedRecords.has(r.id))}
              recordType={selectedRecordType}
              schema={schemas[selectedRecordType]}
              lookupData={lookupData}
              onUpdate={handleBulkUpdate}
              onCancel={handleBulkUpdateComplete}
            />
          )}
        </>
      )}

      {/* Record Details View */}
      {viewDetailsRecord && (
        <div className={styles.detailsOverlay}>
          <div className={styles.detailsContainer}>
            <RecordDetails 
              id={viewDetailsRecord.id}
              record={viewDetailsRecord}
              onClose={() => setViewDetailsRecord(null)}
            />
          </div>
        </div>
      )}

      {/* Success Toast */}
      {toastMessage && (
        <div className={styles.toast}>
          <div className={styles.toastContent}>
            <div className={styles.checkIcon}>✓</div>
            <div className={styles.toastText}>
              <div className={styles.message}>{toastMessage}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionCleanup;